import { LinkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { isEmpty } from 'radash';
import {Control, FieldValues, useFieldArray, UseFormRegister, useWatch} from 'react-hook-form';

import {Button} from '@/components/core/Button';
import {Icon} from '@/components/core/Icon';
import {IconButton} from '@/components/core/IconButton';
import {Input} from '@/components/core/Input';
import { useToast } from '@/hooks/useToast';
import {useTranslation} from '@/hooks/useTranslation.tsx';
import {EnhancedContentRetailer} from '@/pages/enhanced-content/enhanced-content.types.ts';

interface ArticleNumbersProps {
    destinationIndex: number;
    availableRetailers: EnhancedContentRetailer[];
    control: Control<FieldValues>;
    register: UseFormRegister<FieldValues>;
    isReviewer:boolean
}

const ArticleNumbers = ({destinationIndex, availableRetailers, control, register, isReviewer}: ArticleNumbersProps) => {

    const {t} = useTranslation();

    const {toast} = useToast();

    const destination = useWatch({
        control: control,
        name: `retailerLinkIds.${destinationIndex}`
    });

    const {fields, remove, append} = useFieldArray({
        control: control,
        name: `retailerLinkIds.${destinationIndex}.articleNumbers`,
    });

    const onOpenArticle = (index: number) => {

        const articleNumber = destination?.articleNumbers?.[index]?.articleNumber;
        const retailer = availableRetailers.find(retailer => retailer.name === destination?.name);

        if (articleNumber && retailer && !isEmpty(articleNumber) && !isEmpty(retailer.articleTemplateUrl)) {
            const url = retailer.articleTemplateUrl.replace('${articleNumber}', articleNumber);
            window.open(url, '_blank');
        } else {
            toast.info(t('ENHANCED_CONTENT.ARTICLE_URL_NOT_FOUND'));
        }

    };

    return (
        <>
            {fields.map((field, index) => {
                return (
                    <div className="tw-flex tw-flex-wrap tw-my-2 tw-w-full tw-items-center" key={index}>
                        <label className="tw-mb-2 tw-text-t-sm sm:tw-mb-0 sm:tw-w-1/4 sm:tw-pr-2">{t('ENHANCED_CONTENT.ARTICLE_NUMBER')}</label>
                        <Input
                            key={field.id}
                            className="tw-flex-1 sm:tw-w-2/3 tw-ml-2 sm:tw-ml-0 tw-mb-2 sm:tw-mb-0"
                            disabled={(!availableRetailers || isEmpty(destination?.name)) || isReviewer}
                            {...register(
                            `retailerLinkIds.${destinationIndex}.articleNumbers.${index}.articleNumber` as const,
                            {required: true},
                            )}
                        />
                        <IconButton
                            className="tw-ml-2 sm:tw-ml-4 tw-mb-2 sm:tw-mb-0"
                            disabled={!availableRetailers ||
                                isEmpty(destination?.name) ||
                                isEmpty(destination?.articleNumbers?.[index]?.articleNumber)}
                            icon={LinkIcon}
                            variant="default"
                            onClick={(e) => {
                                e.preventDefault();
                                onOpenArticle(index);
                            }}
                        />

                        {!isReviewer && <Button
                            className="tw-mb-2 sm:tw-mb-0 sm:tw-ml-4"
                            onClick={() => {
                                remove(index);
                            }}
                            variant="destructive"
                            disabled={fields.length <= 1}
                        >
                            <Icon icon={TrashIcon} variant="destructive" disabled={fields.length <= 1} size="sm"/>
                        </Button>}

                    </div>
                );
            })}
            {!isReviewer && <Button
                className="tw-w-14 tw-mt-2"
                onClick={() => append({articleNumber: ''})}
            >
                <Icon icon={PlusIcon} variant="light" size="sm"/>
            </Button>}
        </>
    );
};

export {ArticleNumbers};
